import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'

/**
 * Android hardware Back (the ◀ of 3-button navigation, and the edge swipe of gesture nav).
 *
 * The app is one page with no router, so there is no history for Back to pop: Capacitor's
 * default goes straight to "exit the app", and every sheet and drill-down level ignores the
 * button. On a 3-button device that leaves the navigation bar inert.
 *
 * The fix is a LIFO stack of dismiss handlers. Anything backable-out-of registers one WHILE
 * SHOWING; a press runs the most recent, unwinding the UI in the reverse of the order the
 * user built it:
 *
 *   train timetable → that route's next hour → arrivals overview → sheet closed → root
 *
 * Registration order defines the stack, so nothing needs to know another component's levels.
 *
 * All three navigation modes deliver the same `backButton` event, so none of this branches
 * per device — but it does depend on Android's LEGACY back dispatch. At targetSdkVersion 35
 * predictive back is off, so back arrives through Capacitor's `onBackPressed` override.
 * targetSdk 36 turns it on with no opt-out and routes through `OnBackInvokedCallback`
 * instead — **re-test the whole chain at that bump**, and check whether Capacitor has
 * adopted the new dispatcher.
 */

interface Slot {
  run: () => void
}

const stack: Slot[] = []

/**
 * While `active()` is true, Back runs `handler` instead of falling through. `active` is a
 * GETTER, not a boolean, so the effect re-reads it and the registration tracks state:
 * `useBackHandler(() => sheetOpen, ...)`.
 *
 * The slot is pushed on activation and removed on deactivation only. That matters:
 * re-pushing a still-open sheet's handler would float it above the drill-down level opened
 * INSIDE it, and Back would close the whole sheet. `$effect` re-runs only when `active()`
 * actually changes, so that ordering holds for free.
 *
 * `handler` needs no ref indirection: it's an ordinary closure over the caller's `$state`,
 * so invoking it at press time reads current values.
 */
export function useBackHandler(active: () => boolean, handler: () => void) {
  const slot: Slot = { run: () => handler() }

  $effect(() => {
    if (!active()) return
    stack.push(slot)
    return () => {
      const i = stack.lastIndexOf(slot)
      if (i !== -1) stack.splice(i, 1)
    }
  })
}

/** Runs the topmost handler. False means nothing was open — we're at the root. */
function popBack(): boolean {
  const slot = stack[stack.length - 1]
  if (!slot) return false
  slot.run()
  return true
}

/** How long a first root-level Back stays "armed" for the confirming second press. */
const EXIT_HINT_MS = 2000

/**
 * Root Back wiring — mount exactly once, in `App`.
 *
 * Exposes whether the "press back again to exit" hint should show: at the root the first
 * press only arms the exit, so a stray press while reaching for Recents can't kill a live
 * trip. Registering any `backButton` listener suppresses Capacitor's exit-immediately
 * default, which is what this replaces.
 *
 * Returns an object with a getter, not a bare boolean: `return exitHint` would hand back a
 * value snapshotted at call time, severed from later updates.
 */
export function useAndroidBackButton(): { readonly exitHint: boolean } {
  let exitHint = $state(false)
  let armed = false
  let timer: ReturnType<typeof setTimeout> | null = null

  $effect(() => {
    if (Capacitor.getPlatform() !== 'android') return

    const disarm = () => {
      if (timer) { clearTimeout(timer); timer = null }
      armed = false
      exitHint = false
    }

    const pending = App.addListener('backButton', () => {
      // Anything open: unwind one level and cancel a pending exit — the user is navigating,
      // not leaving.
      if (popBack()) { disarm(); return }
      if (armed) { disarm(); void App.exitApp(); return }
      armed = true
      exitHint = true
      timer = setTimeout(disarm, EXIT_HINT_MS)
    })

    return () => {
      // The handle exists only once the bridge round-trip resolves, which can land after
      // teardown — so remove off the promise, not a local.
      void pending.then((h) => h.remove())
      if (timer) clearTimeout(timer)
    }
  })

  return {
    get exitHint() { return exitHint },
  }
}
